import { useState, useEffect, useCallback, useMemo } from "react";
import { HiPencil, HiLockClosed, HiChevronDown, HiArrowsPointingOut, HiXMark } from "react-icons/hi2";
import { useUserPreferences, useMergeBoardContent } from "../hooks/useUserPreferences";
import { useBoardLock, useAcquireLock, useReleaseLock, useRefreshLock } from "../hooks/useBoardLocks";
import { DEFAULT_BOARD_CONTENT } from "../config/boards";
import { useTeamsAndPods, Team, Pod } from "../hooks/useTeamsAndPods";
import { useCurrentUser } from "../hooks/useCurrentUser";
import BoardEditor from "./BoardEditor";
import BoardSkeleton from "./BoardSkeleton";

interface BulletinBoardProps {
  userId: string;
  userName?: string;
}

export default function BulletinBoard({ userId, userName = "Someone" }: BulletinBoardProps) {
  // Fetch teams and pods from dashboard API
  const { teams, pods, isLoading: teamsLoading, errors: teamsErrors } = useTeamsAndPods();

  // Fetch current user's team/pod memberships
  const { user: currentUser, isLoading: userLoading } = useCurrentUser();
  // All users see all teams (except Leadership Team)
  const userTeams = useMemo(() => {
    if (!currentUser) return [];
    return teams.filter((t) => t.teamName !== "Leadership Team");
  }, [teams, currentUser]);

  // All users see all pods
  const userPods = useMemo(() => {
    if (!currentUser) return [];
    return pods;
  }, [pods, currentUser]);

  // Selected team and pod state
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [selectedPod, setSelectedPod] = useState<Pod | null>(null);

  // Set default team/pod based on current user — resets when user identity changes
  const currentUserEmail = currentUser?.email_address;
  useEffect(() => {
    console.log("[BulletinBoard] Default selection effect fired", {
      currentUserEmail,
      currentUserTeamId: currentUser?.teamId,
      currentUserTeamName: currentUser?.team?.teamName,
      currentUserPods: currentUser?.pods?.map((p) => ({ id: p.id, name: p.name })),
      availableTeams: userTeams.map((t) => ({ id: t.id, name: t.teamName })),
      availablePods: userPods.map((p) => ({ id: p.id, name: p.name })),
    });

    if (userTeams.length === 0) {
      console.log("[BulletinBoard] No teams available, clearing selections");
      setSelectedTeam(null);
      setSelectedPod(null);
      return;
    }

    // Default to user's own team, fallback to first team
    const ownTeam = currentUser?.teamId
      ? userTeams.find((t) => t.id === currentUser.teamId)
      : null;
    const teamToSelect = ownTeam || userTeams[0];
    console.log("[BulletinBoard] Selecting team:", {
      ownTeamMatch: ownTeam ? { id: ownTeam.id, name: ownTeam.teamName } : null,
      fallback: !ownTeam,
      selected: { id: teamToSelect.id, name: teamToSelect.teamName },
    });
    setSelectedTeam(teamToSelect);

    // Default to user's own pod, fallback to first pod
    if (userPods.length > 0) {
      const userPodList = currentUser?.pods;
      const ownPod = userPodList?.length
        ? userPods.find((p) => userPodList.some((up: { id: string }) => up.id === p.id))
        : null;
      const podToSelect = ownPod || userPods[0];
      console.log("[BulletinBoard] Selecting pod:", {
        ownPodMatch: ownPod ? { id: ownPod.id, name: ownPod.name } : null,
        fallback: !ownPod,
        selected: { id: podToSelect.id, name: podToSelect.name },
      });
      setSelectedPod(podToSelect);
    } else {
      console.log("[BulletinBoard] No pods available");
      setSelectedPod(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserEmail, userTeams, userPods]);

  // Check if any dropdown is visible (for alignment)
  const hasAnyDropdown = userTeams.length > 1 || userPods.length > 1;

  // Store content overrides - now global, not per-user (PostgreSQL)
  const { data: userPrefs, isLoading: prefsLoading } = useUserPreferences("global-board-content");
  const mergeBoardContentMutation = useMergeBoardContent();

  // Locking (PostgreSQL)
  const acquireLockMutation = useAcquireLock();
  const releaseLockMutation = useReleaseLock();
  const refreshLockMutation = useRefreshLock();

  const [editingBoard, setEditingBoard] = useState<{ id: string; title: string; content: string } | null>(null);
  const [lockError, setLockError] = useState<string | null>(null);
  const [maximizedBoard, setMaximizedBoard] = useState<{ id: string; title: string; content: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Lock body scroll when modal is open (preserve scrollbar space to prevent layout shift)
  useEffect(() => {
    if (maximizedBoard || editingBoard) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [maximizedBoard, editingBoard]);

  // Refresh lock periodically while editing
  useEffect(() => {
    if (!editingBoard) return;

    const interval = setInterval(() => {
      refreshLockMutation.mutate({ boardId: editingBoard.id, userId });
    }, 60000); // Refresh every minute

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBoard?.id, userId]);

  // Release lock on unmount or when closing editor
  useEffect(() => {
    const boardId = editingBoard?.id;
    return () => {
      if (boardId) {
        releaseLockMutation.mutate({ boardId, userId });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBoard?.id, userId]);

  const getBoardContent = (boardId: string) => {
    const prefs = userPrefs as { boardContent?: Record<string, string> } | undefined;
    return prefs?.boardContent?.[boardId] ?? DEFAULT_BOARD_CONTENT[boardId] ?? "";
  };

  const handleEdit = useCallback(async (boardId: string, title: string) => {
    setLockError(null);

    // Try to acquire lock
    const result = await acquireLockMutation.mutateAsync({ boardId, userId, userName });

    if (!result.success) {
      setLockError(result.message || "Board is locked");
      return;
    }

    setEditingBoard({
      id: boardId,
      title,
      content: getBoardContent(boardId),
    });
  }, [acquireLockMutation, userId, userName, userPrefs]);

  const handleSave = async () => {
    if (!editingBoard || isSaving) return;

    setIsSaving(true);
    try {
      // Server-side jsonb_set merge — sends only this board's content, so
      // concurrent saves to different boards can't clobber each other.
      await mergeBoardContentMutation.mutateAsync({
        boardId: editingBoard.id,
        content: editingBoard.content,
      });

      // Release lock
      await releaseLockMutation.mutateAsync({ boardId: editingBoard.id, userId });
      setEditingBoard(null);
    } catch (error) {
      console.error("Failed to save board:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = async () => {
    if (editingBoard) {
      await releaseLockMutation.mutateAsync({ boardId: editingBoard.id, userId });
    }
    setEditingBoard(null);
  };

  // Determine if user is on Account Team (use cached data if available)
  const isAccountTeam = currentUser?.team?.teamName === "Account Team";

  // Show skeleton while loading
  if (prefsLoading) {
    // If we know user's team (from cache), show correct number of boards
    // Otherwise default to 2 (the common case)
    const showPodBoard = isAccountTeam;

    return (
      <div className="flex flex-col lg:flex-row justify-center gap-6">
        <div className="flex flex-col flex-1 min-w-0">
          <div className="h-8 mb-2" />
          <BoardSkeleton />
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <div className="h-8 mb-2 bg-white/10 rounded-lg" />
          <BoardSkeleton />
        </div>
        {showPodBoard && (
          <div className="flex flex-col flex-1 min-w-0">
            <div className="h-8 mb-2 bg-white/10 rounded-lg" />
            <BoardSkeleton />
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Lock error toast */}
      {lockError && (
        <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 rounded-lg text-yellow-800 text-sm flex items-center gap-2">
          <HiLockClosed className="w-4 h-4" />
          {lockError}
          <button
            onClick={() => setLockError(null)}
            className="ml-auto text-yellow-600 hover:text-yellow-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Teams/Pods loading error */}
      {teamsErrors && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-800 text-sm">
          Failed to load teams/pods: {teamsErrors.join(", ")}
        </div>
      )}

      <div className="flex flex-col lg:flex-row justify-center gap-6">
        {/* Card 1: General Board - no dropdown */}
        <div className="flex flex-col flex-1 min-w-0">
          {hasAnyDropdown && <div className="h-8 mb-2" />}
          <BoardCard
            board={{ id: "general", title: "General Board", type: "general" }}
            content={getBoardContent("general")}
            onEdit={() => handleEdit("general", "General Board")}
            onMaximize={() => setMaximizedBoard({ id: "general", title: "General Board", content: getBoardContent("general") })}
            userId={userId}
          />
        </div>

        {/* Card 2: Team Board - hidden if user has no team */}
        {userTeams.length > 0 && (
          <div className="flex flex-col flex-1 min-w-0">
            {userTeams.length > 1 ? (
              <div className="mb-2">
                <Dropdown
                  value={selectedTeam?.id || ""}
                  onChange={(id) => {
                    const team = userTeams.find((t) => t.id === id);
                    setSelectedTeam(team || null);
                  }}
                  options={userTeams.map((t) => ({ id: t.id, label: t.teamName }))}
                  placeholder="Select Team..."
                  isLoading={teamsLoading || userLoading}
                />
              </div>
            ) : (
              hasAnyDropdown && <div className="h-8 mb-2" />
            )}
            {selectedTeam ? (
              <BoardCard
                board={{ id: selectedTeam.id, title: `${selectedTeam.teamName} Board`, type: "team" }}
                content={getBoardContent(selectedTeam.id)}
                onEdit={() => handleEdit(selectedTeam.id, `${selectedTeam.teamName} Board`)}
                onMaximize={() => setMaximizedBoard({ id: selectedTeam.id, title: `${selectedTeam.teamName} Board`, content: getBoardContent(selectedTeam.id) })}
                userId={userId}
              />
            ) : (
              <BoardSkeleton />
            )}
          </div>
        )}

        {/* Card 3: Pod Board - only show when Account Team is selected */}
        {userPods.length > 0 && selectedTeam?.teamName === "Account Team" && (
          <div className="flex flex-col flex-1 min-w-0">
            {userPods.length > 1 ? (
              <div className="mb-2">
                <Dropdown
                  value={selectedPod?.id || ""}
                  onChange={(id) => {
                    const pod = userPods.find((p) => p.id === id);
                    setSelectedPod(pod || null);
                  }}
                  options={userPods.map((p) => ({ id: p.id, label: p.name }))}
                  placeholder="Select Pod..."
                  isLoading={teamsLoading || userLoading}
                />
              </div>
            ) : (
              hasAnyDropdown && <div className="h-8 mb-2" />
            )}
            {selectedPod ? (
              <BoardCard
                board={{
                  id: selectedPod.id,
                  title: `${selectedPod.name} Board`,
                  type: "pod",
                }}
                content={getBoardContent(selectedPod.id)}
                onEdit={() => handleEdit(selectedPod.id, `${selectedPod.name} Board`)}
                onMaximize={() => setMaximizedBoard({ id: selectedPod.id, title: `${selectedPod.name} Board`, content: getBoardContent(selectedPod.id) })}
                userId={userId}
              />
            ) : (
              <BoardSkeleton />
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingBoard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCancel} />
          <div className="relative w-full max-w-2xl mx-4 bg-white dark:bg-maven-gray-800 rounded-lg shadow-xl dark:ring-1 dark:ring-maven-teal-500/40">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-maven-teal-500/40">
              <h2 className="font-medium text-gray-900 dark:text-maven-gray-100">Edit {editingBoard.title}</h2>
            </div>

            <div className="p-4">
              <BoardEditor
                content={editingBoard.content}
                onChange={(content) => setEditingBoard({ ...editingBoard, content })}
                placeholder="Enter board content..."
              />
            </div>

            <div className="px-4 py-3 border-t border-gray-200 dark:border-maven-teal-500/40 flex justify-end gap-2">
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="px-4 py-2 text-sm text-gray-600 dark:text-maven-gray-300 hover:text-gray-800 dark:hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 text-sm bg-maven-teal-500 text-white rounded-lg hover:bg-maven-teal-700 disabled:opacity-50 disabled:cursor-not-allowed min-w-[70px]"
              >
                {isSaving ? (
                  <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Maximize Modal */}
      {maximizedBoard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMaximizedBoard(null)} />
          <div className="relative w-full max-w-4xl h-full max-h-[90vh] bg-white dark:bg-maven-gray-800 rounded-lg shadow-xl flex flex-col dark:ring-1 dark:ring-maven-teal-500/40">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-maven-teal-500/40 flex items-center justify-between shrink-0">
              <h2 className="font-bold text-maven-teal-500 text-2xl">{maximizedBoard.title}</h2>
              <button
                onClick={() => setMaximizedBoard(null)}
                className="p-2 text-gray-400 dark:text-maven-gray-300 hover:text-gray-600 dark:hover:text-white rounded-lg hover:bg-gray-100"
                title="Close"
              >
                <HiXMark className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div
                className="board-content text-base text-maven-gray-900 dark:text-maven-gray-100 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:space-y-2 [&_li]:text-maven-gray-900 dark:[&_li]:text-maven-gray-100 dark:text-maven-gray-100 [&_p]:mb-3 [&_a]:text-blue-600 dark:text-maven-cyan-300 dark:[&_a]:text-maven-cyan-300 [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:my-3"
                dangerouslySetInnerHTML={{ __html: maximizedBoard.content }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Dropdown component for team/pod selection
function Dropdown({
  value,
  onChange,
  options,
  placeholder,
  isLoading,
}: {
  value: string;
  onChange: (id: string) => void;
  options: { id: string; label: string }[];
  placeholder: string;
  isLoading?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find((o) => o.id === value);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="w-full h-8 px-3 flex items-center justify-between bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-lg text-white text-sm transition-colors disabled:opacity-50"
      >
        <span className="truncate">
          {isLoading ? "Loading..." : selectedOption?.label || placeholder}
        </span>
        <HiChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && !isLoading && (
        <>
          {/* Backdrop to close dropdown */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          {/* Dropdown menu */}
          <div className="absolute top-full left-0 right-0 mt-1 bg-white/80 backdrop-blur-md rounded-lg shadow-lg max-h-60 overflow-y-auto z-20">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No options available</div>
            ) : (
              options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => {
                    onChange(option.id);
                    setIsOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 transition-colors ${
                    option.id === value ? "bg-maven-teal-50 text-maven-teal-700 font-medium" : "text-gray-700"
                  }`}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Separate component to subscribe to lock status
function BoardCard({
  board,
  content,
  onEdit,
  onMaximize,
  userId,
}: {
  board: { id: string; title: string; type: string; podName?: string; subPodName?: string };
  content: string;
  onEdit: () => void;
  onMaximize: () => void;
  userId: string;
}) {
  const { data: lock } = useBoardLock(board.id);
  const isLockedByOther = lock && lock.userId !== userId;

  return (
    <div
      className="bg-white dark:bg-maven-gray-800 rounded-lg overflow-hidden group relative flex-1 flex flex-col max-h-[60vh] dark:ring-1 dark:ring-maven-teal-500/40"
    >
      {/* Fixed Header */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-maven-teal-500/30 flex items-center justify-between shrink-0">
        <div>
          {board.podName && board.subPodName ? (
            <>
              <div className="text-gray-400 dark:text-maven-gray-300 font-normal text-sm">{board.podName}</div>
              <div className="font-bold text-maven-teal-500 text-xl">{board.subPodName}</div>
            </>
          ) : (
            <h3 className="font-bold text-maven-teal-500 text-xl">{board.title}</h3>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isLockedByOther && (
            <div className="flex items-center gap-1 bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded-full">
              <HiLockClosed className="w-3 h-3" />
              <span>{lock.userName}</span>
            </div>
          )}
          <button
            onClick={onMaximize}
            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 dark:text-maven-gray-300 hover:text-gray-600 dark:hover:text-white transition-opacity"
            title="Expand board"
          >
            <HiArrowsPointingOut className="w-4 h-4" />
          </button>
          {!isLockedByOther && (
            <button
              onClick={onEdit}
              className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 dark:text-maven-gray-300 hover:text-gray-600 dark:hover:text-white transition-opacity"
              title="Edit board"
            >
              <HiPencil className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content - double-click to edit */}
      <div
        className="px-4 py-3 flex-1 overflow-y-auto cursor-default"
        onDoubleClick={!isLockedByOther ? onEdit : undefined}
        title={!isLockedByOther ? "Double-click to edit" : undefined}
      >
        <div
          className="board-content text-sm text-maven-gray-900 dark:text-maven-gray-100 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:ml-4 [&_ol]:space-y-1 [&_li]:text-maven-gray-900 dark:[&_li]:text-maven-gray-100 [&_p]:mb-2 [&_a]:text-blue-600 dark:text-maven-cyan-300 dark:[&_a]:text-maven-cyan-300 [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:my-2"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    </div>
  );
}
